// AuditoriaGrid.jsx
import React, { useState, useEffect, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@mui/styles";
import { columnsAuditoria } from "./columnsAuditoria";
import { createHandleAuditoriaKeyDown } from "./AuditoriaKeyHandler";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import crypto from "../../Crypto";
import AuditoriaPacientePanel from "./AuditoriaPacientePanel";
import { useAuditoria } from "../contexts/AuditoriaContext";
import ModalInsertarEditarPrestacion from "../modals/ModalInsertarEditarPrestacion";
import DeleteModel from "../../DeleteModel";
import { toast } from "react-toastify";
import { ModeloGetUrlJson } from "../../ModeloCrud";
import dayjs from "dayjs";
import PanelAuditoriaLateral from "./PanelAuditoriaLateral";
import CustomDataGrid from "../../CustomDataGrid";

const useStyles = makeStyles({
  columnHeaders: {
    backgroundColor: "#eceff1 !important",
    borderBottom: "1px solid #ccc !important",
  },
  columnHeaderTitle: {
    fontSize: "0.8rem",
    lineHeight: 1,
  },
});

export default function AuditoriaGrid({
  auditingRows,
  setMotivoRow,
  setMotivoModalOpen,
  aprobarRechazar,
  filtroPrestaciones,
}) {
  const classes = useStyles();
  const history = useHistory();
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [deletePrestacionId, setDeletePrestacionId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelWidth, setPanelWidth] = useState(720);
  const viewportHeight = window.innerHeight;
  console.log("Viewport height:", viewportHeight);
  const gridHeight = viewportHeight - 240;

  const { selectedRow, updateAuditoria, page, selectionModel, modoAuditoria } =
    useAuditoria();

  const handleNew = () => {
    setEditRow(null);
    setModalPrestacionesOpen(true);
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setModalPrestacionesOpen(true);
  };

  const handleDelete = (row) => {
    setDeletePrestacionId(row.tratamiento_id);
  };

  const afterDelete = () => {
    handleDeleteRow(deletePrestacionId);
    toast.success("La prestación se eliminó correctamente.");
  };

  const handleView = (row) => {
    setPacienteSeleccionado({
      nombre: row.nombre_paciente,
      documento: row.documento_paciente,
      id: row.paciente_id,
    });
  };

  const [modalPrestacionesOpen, setModalPrestacionesOpen] = useState(false);

  const hidePrestacionesModal = () => {
    setModalPrestacionesOpen(false);
  };

  const handleRowFocusIn = (param) => {
    updateAuditoria({
      pacienteId: param.row.paciente_id,
      pacienteNombre: param.row.nombre_paciente,
      pacienteDocumento: param.row.documento_paciente,
    });
  };

  const onCellKeyDown = createHandleAuditoriaKeyDown({
    auditingRows,
    updateAuditoria,
    setMotivoRow,
    setMotivoModalOpen,
    aprobarRechazar,
    selectedRow,
    setPacienteSeleccionado,
    modoAuditoria,
  });

  useEffect(() => {
    const logViewportSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
    };
    // Llama a la función cuando el componente se monta
    logViewportSize();
    if (selectedRow) {
      setPanelOpen(true);
    }
  }, [selectedRow]);

  useEffect(() => {
    const tieneRelacionadas = !!selectedRow?.relacionadas?.length;
  }, [selectedRow, panelOpen]);

  const handleDeleteRow = (tratamientoId) => {
    const nuevaLista = auditingRows.filter(
      (r) => r.tratamiento_id !== tratamientoId
    );
    updateAuditoria({ auditingRows: nuevaLista });
  };

  const handleUpdateRow = (updatedRow) => {
    const nuevaLista = auditingRows.map((r) =>
      r.tratamiento_id === updatedRow.tratamiento_id
        ? { ...r, ...updatedRow }
        : r
    );
    updateAuditoria({ auditingRows: nuevaLista });
  };

  const handleAddRow = (nuevaFila, tratamientoBaseId) => {
    const index = auditingRows.findIndex(
      (r) => r.tratamiento_id === tratamientoBaseId
    );
    const nuevaLista =
      index !== -1
        ? [
            ...auditingRows.slice(0, index + 1),
            nuevaFila,
            ...auditingRows.slice(index + 1),
          ]
        : [...auditingRows, nuevaFila];
    updateAuditoria({ auditingRows: nuevaLista });
  };

  const filteredRows = useMemo(() => {
    if (filtroPrestaciones === "Aprobados") {
      return auditingRows.filter((row) =>
        ["X", "O", "L"].includes(row.resultado_auditoria)
      );
    }

    if (filtroPrestaciones === "Rechazados") {
      return auditingRows.filter((row) =>
        ["F", "R", "A"].includes(row.resultado_auditoria)
      );
    }

    return auditingRows;
  }, [auditingRows, filtroPrestaciones]);

  const rowsWithIndex = useMemo(() => {
    return filteredRows.map((row, index) => ({
      ...row,
      index: index + 1,
    }));
  }, [filteredRows]);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <PanelAuditoriaLateral
        prestacion={selectedRow}
        open={panelOpen}
        setOpen={setPanelOpen}
        width={panelWidth}
        setWidth={setPanelWidth}
      />

      <Box
        sx={{
          height: "calc(100vh - 180px)",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={rowsWithIndex}
          columns={columnsAuditoria(
            handleNew,
            handleEdit,
            handleDelete,
            handleView,
            !panelOpen
          )}
          //height={gridHeight}
          getRowId={(row) => row.tratamiento_id}
          pagination
          pageSize={100}
          page={page}
          onPageChange={(newPage) => updateAuditoria({ page: newPage })}
          rowsPerPageOptions={[100]}
          rowHeight={30}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) =>
            updateAuditoria({ selectionModel: newSelection })
          }
          onRowClick={(params) => {
            updateAuditoria({ selectedRow: params.row });
            crypto.encryptDataStorage("searchpatient", params.row.paciente_id);
          }}
          rowSelectionModel={selectedRow ? [selectedRow] : []}
          onCellKeyDown={onCellKeyDown}
          classes={classes}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? "fila-par"
              : "fila-impar"
          }
          rowCount={rowsWithIndex.length}
          paginationMode="client"
          localeText={{
            footerPaginationRowsPerPage: "",
            footerPaginationLabelDisplayedRows: ({ page, count }) =>
              `Página ${page + 1} de ${Math.max(1, Math.ceil(count / 100))}`,
          }}
          componentsProps={{
            pagination: { showFirstButton: true, showLastButton: true },
          }}
          headerHeight={36}
          sx={{
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",

            // Fila par e impar
            "& .MuiDataGrid-row.fila-par": {
              backgroundColor: "#e8eaf6",
            },
            "& .MuiDataGrid-row.fila-impar": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "#c5e1f4 !important",
            },
            "& .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "#b3d5ee !important",
            },

            // Header más bajo
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#fafafa !important",
              height: 28,
              minHeight: 28,
              maxHeight: 28,
              borderBottom: "1px solid #ccc",
            },
            "& .MuiDataGrid-columnHeader": {
              paddingTop: 0,
              paddingBottom: 0,
              fontSize: "0.72rem",
              lineHeight: "1.1rem",
            },
            // Overlay y ajuste del scroll
            "& .MuiDataGrid-overlay": {
              top: "28px",
            },
            "& .MuiDataGrid-virtualScroller": {
              marginTop: 0,
              "&::-webkit-scrollbar": { width: "25px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
                border: "5px solid transparent",
                backgroundClip: "content-box",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
              },
            },
          }}
        />
      </Box>

      <AuditoriaPacientePanel
        open={!!pacienteSeleccionado}
        onClose={() => setPacienteSeleccionado(null)}
        paciente={pacienteSeleccionado}
      />

      <ModalInsertarEditarPrestacion
        showModal={modalPrestacionesOpen && selectedRow}
        hideModal={hidePrestacionesModal}
        row={editRow}
        turnoRow={selectedRow}
        onUpdateRow={handleUpdateRow}
        onAddRow={handleAddRow}
      />

      {deletePrestacionId !== null && (
        <DeleteModel
          model={"tratamiento"}
          modelid={deletePrestacionId}
          okmethod={afterDelete}
          nullmethod={setDeletePrestacionId}
        />
      )}
    </Box>
  );
}
